import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { queries, type AirframeSummary, type LogbookFlight } from "@/api";
import { Chip } from "@/components/Chip";
import { ErrorNote } from "@/components/ErrorNote";
import { LandingCell } from "@/components/FlightRecorder";
import { crewLine, crewRoleLabel, formatDateTime, formatMinutes, yourSeat } from "@/lib/format";

/** The logbook across the airframes (F457, decision 080): the pilot's own flights, or the fleet's, whoever flew them; the aircraft in the air now on top. */
type Scope = "mine" | "fleet";
const PAGE = 50;

export const Route = createFileRoute("/logbook")({
  validateSearch: (search: Record<string, unknown>): { scope: Scope; airframe: string; pilot: string; q: string; page: number } => ({
    scope: search.scope === "fleet" ? "fleet" : "mine",
    airframe: typeof search.airframe === "string" ? search.airframe : "",
    pilot: typeof search.pilot === "string" ? search.pilot : "",
    q: typeof search.q === "string" ? search.q : "",
    page: typeof search.page === "number" && search.page >= 1 ? Math.floor(search.page) : 1,
  }),
  component: Logbook,
});

/** The rows shown as a CSV file, downloaded by the browser (RF-54). */
export function logbookCsv(rows: LogbookFlight[]): string {
  const cell = (v: string | number | null | undefined) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = ["takeoff_utc", "landing_utc", "from", "to", "airframe", "pilot", "enroute_min", "air_distance_nm", "fuel_used_lb", "landing", "exceedances"];
  const lines = rows.map((f) =>
    [f.takeoff_utc, f.landing_utc, f.takeoff_icao, f.landing_icao, f.reg, f.pilot ?? "", f.enroute_min, f.air_distance_nm, f.fuel_used_lb, f.landing_severity ?? "", f.exceedances]
      .map(cell)
      .join(","),
  );
  return [head.join(","), ...lines].join("\n") + "\n";
}

/** Where a flying aircraft is, in a few words (the fleet map's wording). */
function whereNow(a: AirframeSummary): string {
  const p = a.lease?.position;
  if (!p) return "booked, no position yet";
  if (!p.on_ground) return `${Math.round(p.alt_ft).toLocaleString("en-US")} ft, ${Math.round(p.gs_kt)} kt, heading ${Math.round(p.hdg_deg)}`;
  return p.gs_kt >= 2 ? `on the ground, taxiing at ${Math.round(p.gs_kt)} kt` : "on the ground, stopped";
}

function Logbook() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/logbook" });
  const me = useQuery(queries.me());
  // Every ten seconds, like the fleet desk: the aircraft in the air move.
  const hangar = useQuery({ ...queries.airframes("member", ""), refetchInterval: 10_000 });
  const filters = { airframe: search.airframe, pilot: search.pilot, q: search.q };
  const list = useQuery(queries.logbook(search.scope, filters, (search.page - 1) * PAGE, PAGE));
  // The other scope's count for the segmented control: one row is enough.
  const other: Scope = search.scope === "mine" ? "fleet" : "mine";
  const otherList = useQuery(queries.logbook(other, filters, 0, 1));
  const [pilotDraft, setPilotDraft] = useState(search.pilot);
  const [qDraft, setQDraft] = useState(search.q);
  // A filter change starts at page 1; My flights has no pilot filter, so the pilot goes with the scope.
  const set = (patch: Partial<typeof search>) => navigate({ search: { ...search, page: 1, ...(patch.scope === "mine" ? { pilot: "" } : {}), ...patch } });
  const total = list.data?.total ?? 0;
  const counts = { [search.scope]: total, [other]: otherList.data?.total } as Record<Scope, number | undefined>;
  const flying = (hangar.data?.items ?? []).filter((a) => a.lease?.flying);
  const airframes = hangar.data?.items ?? [];
  const from = total === 0 ? 0 : (search.page - 1) * PAGE + 1;
  const to = Math.min(total, search.page * PAGE);
  const pages = Math.max(1, Math.ceil(total / PAGE));
  // A page past the last (an old address, a shorter log): the last page.
  useEffect(() => {
    if (list.data && search.page > pages) navigate({ search: { ...search, page: pages } });
  }, [list.data, search, pages, navigate]);
  const pageNumbers = Array.from({ length: pages }, (_, i) => i + 1).filter((n) => n === 1 || n === pages || Math.abs(n - search.page) <= 1);
  const exportCsv = () => {
    const rows = list.data?.items ?? [];
    const blob = new Blob([logbookCsv(rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `logbook-${search.scope}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-5" data-testid="logbook-page">
      <header className="page-head">
        <div>
          <h1 className="page-title">Logbook</h1>
          <p className="muted text-sm">
            {search.scope === "mine" ? "Every flight you flew, on every airframe." : "Every flight of the airframes you fly, whoever flew them."} {total} flight{total === 1 ? "" : "s"}, all times UTC.
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <button type="button" className="btn" onClick={exportCsv} disabled={!list.data || list.data.items.length === 0} data-testid="logbook-export">
            Export CSV
          </button>
        </div>
      </header>
      {flying.length > 0 ? (
        <section className="card-flush" data-testid="in-the-air">
          <div className="card-head">
            <h2 className="text-[1.0714rem] font-semibold">In the air now</h2>
            <span className="muted text-xs">
              {flying.length} aircraft flying; open one to watch the flight on its map
            </span>
          </div>
          <ul className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
            {flying.map((a) => (
              <li key={a.id} className="flex items-center gap-3 rounded-lg border border-line p-3" data-testid={`flying-${a.reg}`}>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-center gap-2">
                    <Link to="/airframes/$id" params={{ id: a.id }} className="ident text-[0.9643rem] font-semibold">
                      {a.reg}
                    </Link>
                    <span className={`pill ${a.lease?.position && !a.lease.position.on_ground ? "pill-info" : "pill-warn"}`}>
                      {a.lease?.position && !a.lease.position.on_ground ? "airborne" : "on the ground"}
                    </span>
                  </span>
                  <span className="muted truncate text-xs">
                    {a.lease?.holder.name}
                    {a.lease?.holder.id === me.data?.user.id ? " (you)" : ""} · {whereNow(a)}
                  </span>
                </span>
                <Link to="/airframes/$id" params={{ id: a.id }} className="btn btn-sm">
                  Watch
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section className="card-flush">
        <div className="card-head flex-wrap gap-3">
          <div className="segs" role="tablist" aria-label="Whose flights">
            {(["mine", "fleet"] as const).map((s) => (
              <button key={s} type="button" role="tab" aria-selected={search.scope === s} className={`seg ${search.scope === s ? "seg-active" : ""}`} onClick={() => set({ scope: s })} data-testid={`logbook-scope-${s}`}>
                {s === "mine" ? "My flights" : "Fleet"}
                <span className="seg-count">{counts[s] ?? "…"}</span>
              </button>
            ))}
          </div>
          <select className="select !w-auto" value={search.airframe} onChange={(e) => set({ airframe: e.target.value })} aria-label="Airframe" data-testid="logbook-airframe">
            <option value="">All airframes</option>
            {airframes.map((a) => (
              <option key={a.id} value={a.id}>
                {a.reg}
                {a.name ? ` · ${a.name}` : ""}
              </option>
            ))}
          </select>
          {search.scope === "fleet" ? (
            <form
              className="contents"
              onSubmit={(e) => {
                e.preventDefault();
                set({ pilot: pilotDraft.trim() });
              }}
            >
              <input className="input !w-40" value={pilotDraft} onChange={(e) => setPilotDraft(e.target.value)} placeholder="Pilot" aria-label="Pilot" data-testid="logbook-pilot" />
            </form>
          ) : null}
          <form
            className="ml-auto contents"
            onSubmit={(e) => {
              e.preventDefault();
              set({ q: qDraft.trim() });
            }}
          >
            <input className="input !w-56" value={qDraft} onChange={(e) => setQDraft(e.target.value)} placeholder="Search airport, registration" aria-label="Search flights" data-testid="logbook-search" />
          </form>
        </div>
        <ErrorNote error={list.error} />
        {list.data && list.data.items.length === 0 ? (
          <p className="muted p-5 text-sm" data-testid="logbook-empty">
            {search.scope === "mine" ? "No flight of yours in the log yet: book an aircraft, fly, return it." : "No flight in the fleet's log matches."}
          </p>
        ) : null}
        {list.data && list.data.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table" data-testid="logbook-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Airframe</th>
                  <th>Route</th>
                  <th>Pilot</th>
                  <th className="text-right">Enroute</th>
                  <th className="text-right">Distance</th>
                  <th>Landing</th>
                  <th>Recorder</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((f) => (
                  <tr key={f.id} className="table-row-link" onClick={() => navigate({ to: "/airframes/$id/flights/$fid", params: { id: f.airframe_id, fid: f.id } })} data-testid="logbook-row">
                    <td className="tnum whitespace-nowrap">{formatDateTime(f.takeoff_utc)}</td>
                    <td>
                      <Link to="/airframes/$id" params={{ id: f.airframe_id }} className="ident" onClick={(e) => e.stopPropagation()}>
                        {f.reg}
                      </Link>
                      {f.airframe_name ? <div className="muted text-xs">{f.airframe_name}</div> : null}
                    </td>
                    <td className="whitespace-nowrap">
                      <span className="ident">{f.takeoff_icao || "----"}</span>
                      <span className="muted mx-2" aria-hidden="true">
                        ⟶
                      </span>
                      <span className="ident">{f.landing_icao || "----"}</span>
                    </td>
                    <td>
                      {f.pilot ?? <span className="muted">history</span>}
                      {(() => {
                        const seat = yourSeat(f.crew);
                        const guests = f.crew.filter((c) => c.guest).length;
                        if (seat) {
                          return (
                            <Chip tone="ok" title={crewLine(f.crew)}>
                              you aboard, {crewRoleLabel(seat.role, seat.pilot_seat)}
                            </Chip>
                          );
                        }
                        return guests > 0 ? (
                          <span className="muted block text-xs" title={crewLine(f.crew)} data-testid="logbook-aboard">
                            {guests} aboard
                          </span>
                        ) : null;
                      })()}
                    </td>
                    <td className="tnum text-right">{formatMinutes(f.enroute_min)}</td>
                    <td className="tnum text-right">{f.air_distance_nm} nm</td>
                    <td>
                      <LandingCell flight={f} />
                    </td>
                    <td>
                      {f.has_track ? (
                        <Chip tone={f.exceedances > 0 ? "bad" : "ok"}>{f.exceedances === 0 ? "within the limits" : `${f.exceedances} exceeded`}</Chip>
                      ) : (
                        <span className="muted text-xs">not recorded</span>
                      )}
                    </td>
                    <td className="text-right">
                      <Link to="/airframes/$id/flights/$fid" params={{ id: f.airframe_id, fid: f.id }} className="muted" aria-label="Open the flight" onClick={(e) => e.stopPropagation()} data-testid="open-flight">
                        ›
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {list.data ? (
          <div className="pager" data-testid="logbook-pager">
            <span>
              {from}–{to} of {total} flight{total === 1 ? "" : "s"}
            </span>
            <span className="ml-auto flex items-center gap-1">
              <button type="button" className="btn" disabled={search.page <= 1} onClick={() => navigate({ search: { ...search, page: search.page - 1 } })}>
                Previous
              </button>
              {pageNumbers.map((n, i) => (
                <span key={n} className="flex items-center gap-1">
                  {i > 0 && pageNumbers[i - 1] !== n - 1 ? <span className="muted px-1">…</span> : null}
                  <button type="button" className={`btn ${n === search.page ? "btn-page-active" : ""}`} aria-current={n === search.page ? "page" : undefined} onClick={() => navigate({ search: { ...search, page: n } })}>
                    {n}
                  </button>
                </span>
              ))}
              <button type="button" className="btn" disabled={search.page >= pages} onClick={() => navigate({ search: { ...search, page: search.page + 1 } })}>
                Next
              </button>
            </span>
          </div>
        ) : null}
      </section>
    </div>
  );
}
